import React from 'react';
import { View, Pressable, Platform, FlatList } from 'react-native';
import { XStack, YStack, Text } from 'tamagui';
import { MaterialCommunityIcons } from '@expo/vector-icons';

interface SuggestedModelCardProps {
  model: {
    _id?: string;
    schemaTitle?: string;
    title?: string;
    description?: string;
    dataTags?: string[];
    schemaContent?: any;
    isSuggested?: boolean;
    modelType?: string;
  };
  theme: boolean;
  isSelected: boolean;
  onToggle: (modelId: string) => void;
}

const SuggestedModelCard: React.FC<SuggestedModelCardProps> = ({
  model,
  theme,
  isSelected,
  onToggle,
}) => {
  const modelId = model._id || model.schemaTitle || 'unknown';
  const modelTitle = model.schemaTitle || model.title || 'Model';
  const modelDescription = model.description || 'Data model for your business';

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    border: isSelected 
      ? (theme ? '#3b82f6' : '#60a5fa')
      : (theme ? '#e5e7eb' : '#4b5563'),
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    accent: '#3b82f6',
    success: '#10b981',
    selectedBackground: theme ? '#eff6ff' : '#1e3a5f',
  };

  return (
    <Pressable
      onPress={() => onToggle(modelId)}
      style={{
        width: Platform.OS === 'web' ? 280 : 240,
        backgroundColor: isSelected ? cardColors.selectedBackground : cardColors.background,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: cardColors.border,
        padding: 16,
        marginRight: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: isSelected ? 0.2 : 0.1,
        shadowRadius: 4,
        elevation: isSelected ? 5 : 3,
      }}
    >
      <XStack alignItems="flex-start" gap={12} marginBottom={12}>
        {/* Checkbox */}
        <View
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            borderWidth: 2,
            borderColor: isSelected ? cardColors.accent : cardColors.border,
            backgroundColor: isSelected ? cardColors.accent : 'transparent',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 2,
          }}
        >
          {isSelected && (
            <MaterialCommunityIcons
              name="check"
              size={16}
              color="white"
            />
          )}
        </View>

        {/* Model Icon */}
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 24,
            backgroundColor: theme ? '#f3f4f6' : '#4b5563',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <MaterialCommunityIcons
            name="database"
            size={24}
            color={cardColors.accent}
          />
        </View>

        <YStack flex={1}>
          <Text
            fontSize={16}
            fontWeight="600"
            color={cardColors.text}
            numberOfLines={1}
          >
            {modelTitle}
          </Text>
          {isSelected && (
            <XStack alignItems="center" gap={4} marginTop={4}>
              <MaterialCommunityIcons
                name="check-circle"
                size={14}
                color={cardColors.success}
              />
              <Text fontSize={12} color={cardColors.success}>
                Selected
              </Text>
            </XStack>
          )}
        </YStack>
      </XStack>

      <Text
        fontSize={12}
        color={cardColors.textSecondary}
        numberOfLines={2}
        marginBottom={12}
      >
        {modelDescription}
      </Text>

      {model.dataTags && model.dataTags.length > 0 && (
        <XStack flexWrap="wrap" gap={4} marginTop={8}>
          {model.dataTags.slice(0, 3).map((tag, index) => (
            <View
              key={index}
              style={{
                backgroundColor: theme ? '#f0f9ff' : '#1e3a5f',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 4,
              }}
            >
              <Text fontSize={10} color={theme ? '#0369a1' : '#93c5fd'}>
                {tag}
              </Text>
            </View>
          ))}
        </XStack>
      )}
    </Pressable>
  );
};

interface SuggestedModelsListProps {
  models: SuggestedModelCardProps['model'][];
  theme: boolean;
  selectedModelIds: string[];
  onModelToggle: (modelId: string) => void;
  onContinue: (selectedModels: SuggestedModelCardProps['model'][]) => void;
  onSkip: () => void;
}

export const SuggestedModelsList: React.FC<SuggestedModelsListProps> = ({
  models,
  theme,
  selectedModelIds,
  onModelToggle,
  onContinue,
  onSkip,
}) => {
  const selectedModels = models.filter(model => {
    const modelId = model._id || model.schemaTitle || 'unknown';
    return selectedModelIds.includes(modelId);
  });

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    accent: '#3b82f6',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
  };

  return (
    <View style={{ width: '100%' }}>
      <View style={{ marginBottom: 16 }}>
        <Text
          fontSize={14}
          fontWeight="600"
          color={cardColors.text}
          marginBottom={8}
        >
          Select the models you'd like to start with:
        </Text>
        <Text
          fontSize={12}
          color={cardColors.textSecondary}
          marginBottom={12}
        >
          {selectedModelIds.length > 0
            ? `${selectedModelIds.length} model${selectedModelIds.length > 1 ? 's' : ''} selected`
            : 'No models selected'}
        </Text>
      </View>

      <View style={{ marginBottom: 16 }}>
        <FlatList
          data={models}
          horizontal
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item, index) => item._id || item.schemaTitle || `model-${index}`}
          renderItem={({ item, index }) => {
            const modelId = item._id || item.schemaTitle || `model-${index}`;
            return (
              <SuggestedModelCard
                model={item}
                theme={theme}
                isSelected={selectedModelIds.includes(modelId)}
                onToggle={onModelToggle}
              />
            );
          }}
          contentContainerStyle={{
            paddingHorizontal: 4,
            paddingVertical: 0,
            paddingBottom: 4,
          }}
        />
      </View>

      <XStack gap={12} marginTop={8}>
        <Pressable
          onPress={() => onContinue(selectedModels)}
          disabled={selectedModelIds.length === 0}
          style={{
            flex: 1,
            backgroundColor: selectedModelIds.length > 0 ? cardColors.accent : cardColors.textSecondary,
            paddingVertical: 12,
            paddingHorizontal: 16,
            borderRadius: 8,
            alignItems: 'center',
            opacity: selectedModelIds.length === 0 ? 0.5 : 1,
          }}
        >
          <Text fontSize={14} fontWeight="600" color="white">
            {selectedModelIds.length > 0
              ? `Continue with ${selectedModelIds.length} model${selectedModelIds.length > 1 ? 's' : ''}`
              : 'Select models to continue'}
          </Text>
        </Pressable>
        <Pressable
          onPress={onSkip}
          style={{
            flex: 1,
            backgroundColor: theme ? '#e5e7eb' : '#4b5563',
            paddingVertical: 12,
            paddingHorizontal: 16,
            borderRadius: 8,
            alignItems: 'center',
          }}
        >
          <Text fontSize={14} fontWeight="600" color={cardColors.text}>
            Skip
          </Text>
        </Pressable>
      </XStack>
    </View>
  );
};

export default SuggestedModelCard;

